import { STATUS } from "@/constants/statuses";
import {
    ReviewTranslationRequestEntity,
    ReviewTranslationResponseEntity,
    ReviewTranslationSupportLangRequestEntity,
    ReviewTranslationSupportLangResponseEntity,
} from "@apps/reviews/entities/ReviewTranslationEntity";
import {
    ReviewTranslationResponseSchema,
    ReviewTranslationSuportLangResponseSchema,
} from "@apps/reviews/schemas/ReviewTranlationSchema";
import { applyTransaction } from "@datorama/akita";
import { JsonMimeType } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ReviewsTranslationsStore from "../stores/ReviewsTranslationsStore";
import {
    reviewsApplicationLoggerToken,
    reviewsTranslationsAuthApiClientToken,
} from "../tokens";

export type ReviewRequestParams = {
    locale: string;
};

@singleton()
class ReviewsTranslationsService {
    constructor(
        private readonly reviewsTranslationsStore: ReviewsTranslationsStore,
        @inject(reviewsTranslationsAuthApiClientToken)
        private readonly reviewsTranslationAuthApiClient: ApiClientInterface,
        @inject(reviewsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    seeTranslation({
        reviewId,
        targetLanguage,
    }: ReviewTranslationRequestEntity): Observable<ReviewTranslationResponseEntity> {
        this.reviewsTranslationsStore.setStatus(STATUS.LOADING);
        this.reviewsTranslationsStore.update((state) => {
            return {
                ...state,
                pendingIds: [...state.pendingIds, reviewId],
            };
        });

        return this.reviewsTranslationAuthApiClient
            .post({
                url: "/translate/review",
                body: JSON.stringify({ reviewId, targetLanguage }),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((res) => create(res.body, ReviewTranslationResponseSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "ReviewsTranslationsService.seeTranslation is errored"
                    );
                    applyTransaction(() => {
                        this.reviewsTranslationsStore.setError(error as Error);
                        this.reviewsTranslationsStore.setStatus(STATUS.ERROR);
                    });
                    throw error;
                }),
                tap((translated) => {
                    this.logger.debug(
                        "ReviewsTranslationsService.seeTranslation is ready"
                    );
                    applyTransaction(() => {
                        this.reviewsTranslationsStore.update((state) => {
                            const existingTrIndex =
                                state.translations.findIndex(
                                    (
                                        translation: ReviewTranslationResponseEntity
                                    ) =>
                                        translation.reviewId ===
                                        translated.reviewId
                                );

                            if (existingTrIndex !== -1) {
                                const updatedTranslations = [
                                    ...state.translations,
                                ];
                                updatedTranslations[existingTrIndex] =
                                    translated;

                                return {
                                    ...state,
                                    translations: updatedTranslations,
                                    pendingIds: state.pendingIds.filter(
                                        (id) => id !== reviewId
                                    ),
                                };
                            } else {
                                return {
                                    ...state,
                                    translations: [
                                        ...state.translations,
                                        translated,
                                    ],
                                    pendingIds: state.pendingIds.filter(
                                        (id) => id !== reviewId
                                    ),
                                };
                            }
                        });
                        this.reviewsTranslationsStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    checkIfLocaleSupported(
        reqParams?: ReviewTranslationSupportLangRequestEntity
    ): Observable<ReviewTranslationSupportLangResponseEntity> {
        this.logger.debug(
            "ReviewsTranslationsService.checkLocaleTranslateReview is started"
        );
        this.reviewsTranslationsStore.update((state) => ({
            ...state,
            checkLocaleStatus: STATUS.LOADING,
        }));

        return this.reviewsTranslationAuthApiClient
            .post({
                url: "/locales/checkLocale",
                body: JSON.stringify(reqParams),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((v) =>
                    create(v.body, ReviewTranslationSuportLangResponseSchema)
                ),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "ReviewsTranslationsService.checkLocaleTranslateReview is errored",
                        error
                    );
                    this.reviewsTranslationsStore.update((state) => ({
                        ...state,
                        checkLocaleStatus: STATUS.ERROR,
                    }));
                    throw error;
                }),
                tap((v) => {
                    this.logger.debug(
                        "ReviewsTranslationsService.checkLocaleTranslateReview is ready"
                    );
                    applyTransaction(() => {
                        this.reviewsTranslationsStore.update((state) => ({
                            ...state,
                            isLocaleSupported: v.supported,
                        }));
                        this.reviewsTranslationsStore.setCheckLocaleStatus(
                            STATUS.READY
                        );
                    });
                })
            );
    }
}

export default ReviewsTranslationsService;
